import React, { useState } from 'react';
import {
  MdCheck,
  MdTextFields,
  MdBarChart,
  MdHorizontalRule,
  MdInsertPageBreak,
  MdExpandMore,
} from 'react-icons/md';
import { IoBarChartSharp } from 'react-icons/io5';
import { FaMoneyBillTrendUp } from 'react-icons/fa6';

import { FaTable } from 'react-icons/fa';
import { IoIosClose } from 'react-icons/io';
import { LuHeading } from 'react-icons/lu';
import { AnimatePresence, motion } from 'framer-motion';
import Tooltip from '@mui/material/Tooltip';
import { PdfTag, isSingletonBlockTag } from '../types/KidPdfGeneratorV2.types';
import {
  staggeredFadeUpAnimation,
  expandCollapseFastAnimation,
} from '../../../../ui/animations/layout.animations';

interface AddBlockChooserProps {
  onChoose: (type: PdfTag) => void;
  existingTags?: ReadonlySet<string>;
}

interface BlockButton {
  type: PdfTag;
  icon: React.ComponentType<{ className?: string }>;
  className: string;
  title: string;
}

interface KidFiguresBlock {
  type: PdfTag;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  description: string;
}

const CONTENT_BLOCKS: BlockButton[] = [
  {
    type: 'text',
    icon: MdTextFields,
    className:
      'border-blue-300 text-blue-600 hover:border-blue-400 hover:bg-blue-50',
    title: 'Add Text Block',
  },
  {
    type: 'heading',
    icon: LuHeading,
    className:
      'border-cyan-300 text-cyan-600 hover:border-cyan-400 hover:bg-cyan-50',
    title: 'Add Heading',
  },
  {
    type: 'table',
    icon: FaTable,
    className:
      'border-rose-300 text-rose-600 hover:border-rose-400 hover:bg-rose-50',
    title: 'Add Table Block',
  },
  {
    type: 'divider',
    icon: MdHorizontalRule,
    className:
      'border-gray-300 text-gray-600 hover:border-gray-400 hover:bg-gray-50',
    title: 'Add Divider',
  },
  {
    type: 'page_break',
    icon: MdInsertPageBreak,
    className:
      'border-slate-300 text-slate-600 hover:border-slate-400 hover:bg-slate-50',
    title: 'Add Page Break',
  },
];

/** Marker-seeded KID preset blocks — singletons, auto-filled from fund figures. */
const KID_FIGURES_BLOCKS: KidFiguresBlock[] = [
  {
    type: 'sri_chart',
    icon: MdBarChart,
    label: 'SRI',
    description: 'Summary Risk Indicator scale (1–7)',
  },
  {
    type: 'costs_over_time_table',
    icon: IoBarChartSharp,
    label: 'Costs over time',
    description: 'Total costs and RIY for each period',
  },
  {
    type: 'composition_of_costs_table',
    icon: FaTable,
    label: 'Composition of costs',
    description: 'Entry, exit, ongoing, and incidental fees',
  },
  {
    type: 'performance_scenarios_table',
    icon: FaMoneyBillTrendUp,
    label: 'Performance scenarios',
    description: 'Stress to favourable return outcomes',
  },
];

const buttonBase =
  'group relative flex items-center justify-center rounded-md border-2 bg-white px-6 py-1 transition-all hover:shadow-sm';
const buttonDisabled =
  'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400 hover:border-gray-200 hover:bg-gray-100 hover:shadow-none';

const BlockButtonGrid: React.FC<{
  buttons: BlockButton[];
  existingTags: ReadonlySet<string>;
  onChoose: (type: PdfTag) => void;
}> = ({ buttons, existingTags, onChoose }) => (
  <div className="flex flex-wrap gap-1">
    {buttons.map((button, index) => {
      const IconComponent = button.icon;
      const isDisabled =
        isSingletonBlockTag(button.type) && existingTags.has(button.type);
      const tooltipTitle = isDisabled
        ? `${button.title} (already added)`
        : button.title;

      return (
        <Tooltip
          key={button.type}
          title={tooltipTitle}
          arrow
          placement="top"
          slotProps={{
            arrow: {
              className: 'text-gray-700',
            },
          }}
        >
          <span className="inline-flex">
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{
                delay: index * 0.02,
                duration: 1,
                ease: [0.4, 0, 0.2, 1],
              }}
              type="button"
              disabled={isDisabled}
              onClick={() => {
                if (isDisabled) return;
                onChoose(button.type);
              }}
              className={`${buttonBase} ${isDisabled ? buttonDisabled : button.className}`}
            >
              <IconComponent className="h-5 w-5" />
              {isDisabled && (
                <MdCheck className="absolute -right-1.5 -top-1.5 h-4 w-4 rounded-full bg-gray-400 p-0.5 text-white" />
              )}
            </motion.button>
          </span>
        </Tooltip>
      );
    })}
  </div>
);

const KidFiguresBlockGrid: React.FC<{
  blocks: KidFiguresBlock[];
  existingTags: ReadonlySet<string>;
  onChoose: (type: PdfTag) => void;
}> = ({ blocks, existingTags, onChoose }) => (
  <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
    {blocks.map((block, index) => {
      const IconComponent = block.icon;
      const isDisabled =
        isSingletonBlockTag(block.type) && existingTags.has(block.type);
      const animation = staggeredFadeUpAnimation(index);

      return (
        <motion.button
          key={block.type}
          initial={animation.initial}
          animate={animation.animate}
          exit={animation.exit}
          transition={animation.transition}
          type="button"
          disabled={isDisabled}
          onClick={() => {
            if (isDisabled) return;
            onChoose(block.type);
          }}
          className={`group relative flex items-start gap-2.5 rounded-md border px-2.5 py-2 text-left transition-colors ${isDisabled
              ? 'cursor-not-allowed border-slate-200 bg-slate-100/80 text-slate-400'
              : 'border-slate-200 bg-white text-slate-800 hover:border-slate-400 hover:bg-slate-50'
            }`}
        >
          <span
            className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded border ${isDisabled
                ? 'border-slate-200 bg-slate-50 text-slate-400'
                : 'border-emerald-200 bg-emerald-50 text-emerald-600 group-hover:border-emerald-300 group-hover:text-emerald-700'
              }`}
          >
            <IconComponent className="h-4 w-4" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-1.5">
              <span className="text-xs font-semibold leading-tight">
                {block.label}
              </span>
              {isDisabled && (
                <span className="rounded bg-slate-200 px-1 py-px text-[9px] font-medium uppercase tracking-wide text-slate-500">
                  Added
                </span>
              )}
            </span>
            <span
              className={`mt-0.5 block text-[11px] leading-snug ${isDisabled ? 'text-slate-400' : 'text-slate-500'
                }`}
            >
              {block.description}
            </span>
          </span>
        </motion.button>
      );
    })}
  </div>
);

const AddBlockChooser: React.FC<AddBlockChooserProps> = ({
  onChoose,
  existingTags = new Set(),
}) => {
  const [open, setOpen] = useState(false);
  const [kidFiguresOpen, setKidFiguresOpen] = useState(false);

  const handleChoose = (type: PdfTag) => {
    onChoose(type);
    setOpen(false);
    setKidFiguresOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
    setKidFiguresOpen(false);
  };

  return (
    <AnimatePresence>
      {!open ? (
        <motion.button
          key="add-block-button"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0 } }}
          transition={{ duration: 0.05, ease: 'easeOut' }}
          type="button"
          onClick={() => setOpen(true)}
          className="my-2 w-full rounded-md border border-dashed border-gray-300 bg-white py-2 text-sm text-gray-600 hover:border-gray-400"
        >
          + Add Block
        </motion.button>
      ) : (
        <motion.div
          key="add-block-chooser"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0 } }}
          transition={{ duration: 0.05, ease: 'easeOut' }}
          className="relative my-2 rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
        >
          <div className="flex flex-col gap-4">
            <div>
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  Content blocks
                </p>
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex h-6 w-6 items-center justify-center rounded text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                  title="Close"
                >
                  <IoIosClose className="h-5 w-5" />
                </button>
              </div>
              <BlockButtonGrid
                buttons={CONTENT_BLOCKS}
                existingTags={existingTags}
                onChoose={handleChoose}
              />
            </div>

            <div className="overflow-hidden rounded-md border border-slate-200 bg-slate-50/80">
              <button
                type="button"
                onClick={() => setKidFiguresOpen((prev) => !prev)}
                className="flex w-full items-center justify-between gap-2 px-2.5 py-2 text-left transition-colors hover:bg-slate-100/80"
                aria-expanded={kidFiguresOpen}
              >
                <span>
                  <span className="block text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                    KID Figures
                  </span>
                  <span className="mt-0.5 block text-[11px] leading-snug text-slate-500">
                    Auto-filled from RiskSystem calculations · one of each per
                    document
                  </span>
                </span>
                <MdExpandMore
                  className={`h-5 w-5 shrink-0 text-slate-500 transition-transform duration-200 ${
                    kidFiguresOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              <AnimatePresence initial={false}>
                {kidFiguresOpen && (
                  <motion.div
                    key="kid-figures-panel"
                    initial={expandCollapseFastAnimation.initial}
                    animate={expandCollapseFastAnimation.animate}
                    exit={expandCollapseFastAnimation.exit}
                    transition={expandCollapseFastAnimation.transition}
                    className="overflow-hidden"
                  >
                    <div className="border-t border-slate-200 px-2.5 pb-2.5 pt-2">
                      <KidFiguresBlockGrid
                        blocks={KID_FIGURES_BLOCKS}
                        existingTags={existingTags}
                        onChoose={handleChoose}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AddBlockChooser;
